import Inventory2Icon from '@mui/icons-material/Inventory2';
import {
  PRODUCT_UNITS,
  createProductSchema,
  formatMoney,
  type CreateProductInput,
  type Product,
} from '@fdt/shared';
import { Chip, MenuItem, Stack, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { DataTable, type Column } from '../components/DataTable';
import { ExportButton } from '../components/ExportButton';
import { FormDialog } from '../components/FormDialog';
import { RowActions } from '../components/RowActions';
import { useCrud } from '../hooks/useCrud';
import { useFormError } from '../hooks/useFormError';
import { currentLocale } from '../i18n';
import { fetchCategories, productsApi } from '../resources';

type FormState = {
  sku: string;
  name: string;
  categoryId: string;
  unit: CreateProductInput['unit'];
  unitsPerCase: string;
  price: string;
  vatRate: string;
  notes: string;
  isActive: boolean;
};

const EMPTY_FORM: FormState = {
  sku: '',
  name: '',
  categoryId: '',
  unit: 'piece',
  unitsPerCase: '1',
  price: '',
  vatRate: '22',
  notes: '',
  isActive: true,
};

export function ProductsPage() {
  const { t } = useTranslation();
  const locale = currentLocale();

  const [categoryFilter, setCategoryFilter] = useState('');
  const [activeFilter, setActiveFilter] = useState('');

  const filters = useMemo(
    () => ({
      categoryId: categoryFilter || undefined,
      isActive: activeFilter || undefined,
    }),
    [categoryFilter, activeFilter],
  );

  const crud = useCrud('products', productsApi, { filters, defaultSortBy: 'name' });
  const categories = useQuery({ queryKey: ['product-categories'], queryFn: fetchCategories });

  const [editing, setEditing] = useState<Product | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const formError = useFormError();

  const [deleting, setDeleting] = useState<Product | null>(null);
  const deleteError = useFormError();

  function openCreate() {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFieldErrors({});
    formError.clear();
    setFormOpen(true);
  }

  function openEdit(product: Product) {
    setEditing(product);
    setForm({
      sku: product.sku,
      name: product.name,
      categoryId: product.categoryId ?? '',
      unit: product.unit,
      unitsPerCase: String(product.unitsPerCase),
      price: product.price,
      vatRate: String(product.vatRate),
      notes: product.notes ?? '',
      isActive: product.isActive,
    });
    setFieldErrors({});
    formError.clear();
    setFormOpen(true);
  }

  async function submit() {
    formError.clear();

    // Validated with the same schema the API uses, so the two cannot drift.
    const parsed = createProductSchema.safeParse({
      sku: form.sku,
      name: form.name,
      categoryId: form.categoryId || null,
      unit: form.unit,
      unitsPerCase: form.unitsPerCase,
      price: form.price,
      vatRate: form.vatRate,
      notes: form.notes || null,
      isActive: form.isActive,
    });

    if (!parsed.success) {
      setFieldErrors(
        Object.fromEntries(
          parsed.error.issues.map((issue) => [
            String(issue.path[0]),
            issue.message.startsWith('validation.') ? issue.message : 'validation.invalid',
          ]),
        ),
      );
      return;
    }

    setFieldErrors({});
    try {
      if (editing) {
        await crud.update.mutateAsync({ id: editing.id, body: parsed.data });
      } else {
        await crud.create.mutateAsync(parsed.data);
      }
      setFormOpen(false);
    } catch (error) {
      formError.capture(error);
    }
  }

  async function confirmDelete() {
    if (!deleting) {
      return;
    }
    deleteError.clear();
    try {
      await crud.remove.mutateAsync(deleting.id);
      setDeleting(null);
    } catch (error) {
      deleteError.capture(error);
    }
  }

  const columns: Column<Product>[] = [
    { id: 'sku', label: t('fields.sku'), render: (row) => row.sku, sortable: true },
    { id: 'name', label: t('fields.name'), render: (row) => row.name, sortable: true },
    {
      id: 'categoryName',
      label: t('fields.category'),
      render: (row) => row.categoryName ?? '—',
    },
    { id: 'unit', label: t('fields.unit'), render: (row) => t(`units.${row.unit}`) },
    {
      id: 'unitsPerCase',
      label: t('fields.unitsPerCase'),
      render: (row) => row.unitsPerCase,
      align: 'right',
    },
    {
      id: 'price',
      label: t('fields.priceNet'),
      render: (row) => formatMoney(row.price, locale),
      align: 'right',
      sortable: true,
    },
    {
      id: 'vatRate',
      label: t('fields.vatRate'),
      render: (row) => `${row.vatRate}%`,
      align: 'right',
    },
    {
      id: 'isActive',
      label: t('fields.status'),
      render: (row) => (
        <Chip
          size="small"
          label={row.isActive ? t('common.active') : t('common.inactive')}
          color={row.isActive ? 'success' : 'default'}
          variant={row.isActive ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      id: 'actions',
      label: t('common.actions'),
      align: 'right',
      width: 100,
      render: (row) => <RowActions onEdit={() => openEdit(row)} onDelete={() => setDeleting(row)} />,
    },
  ];

  return (
    <>
      <DataTable
        title={t('nav.products')}
        icon={Inventory2Icon}
        tone="info"
        columns={columns}
        query={crud.list}
        search={crud.search}
        onSearchChange={crud.setSearch}
        sortBy={crud.sortBy}
        sortDir={crud.sortDir}
        onSort={crud.toggleSort}
        page={crud.page}
        onPageChange={crud.setPage}
        pageSize={crud.pageSize}
        onPageSizeChange={crud.setPageSize}
        rowKey={(row) => row.id}
        onCreate={openCreate}
        toolbar={
          <>
            <TextField
              select
              label={t('fields.category')}
              value={categoryFilter}
              onChange={(event) => setCategoryFilter(event.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              {(categories.data ?? []).map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={t('fields.status')}
              value={activeFilter}
              onChange={(event) => setActiveFilter(event.target.value)}
              sx={{ minWidth: 140 }}
            >
              <MenuItem value="">{t('common.all')}</MenuItem>
              <MenuItem value="true">{t('common.active')}</MenuItem>
              <MenuItem value="false">{t('common.inactive')}</MenuItem>
            </TextField>
            <ExportButton
              file="products.xlsx"
              params={{ categoryId: categoryFilter, isActive: activeFilter }}
            />
          </>
        }
      />

      <FormDialog
        open={formOpen}
        title={editing ? t('products.editTitle') : t('products.createTitle')}
        onClose={() => setFormOpen(false)}
        onSubmit={() => void submit()}
        submitting={crud.create.isPending || crud.update.isPending}
        errorKey={formError.errorKey}
        maxWidth="md"
      >
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('fields.sku')}
              value={form.sku}
              onChange={(event) => setForm({ ...form, sku: event.target.value })}
              error={Boolean(fieldErrors.sku)}
              helperText={fieldErrors.sku ? t(fieldErrors.sku) : undefined}
              required
              fullWidth
            />
            <TextField
              label={t('fields.name')}
              value={form.name}
              onChange={(event) => setForm({ ...form, name: event.target.value })}
              error={Boolean(fieldErrors.name)}
              helperText={fieldErrors.name ? t(fieldErrors.name) : undefined}
              required
              fullWidth
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              label={t('fields.category')}
              value={form.categoryId}
              onChange={(event) => setForm({ ...form, categoryId: event.target.value })}
              fullWidth
            >
              <MenuItem value="">—</MenuItem>
              {(categories.data ?? []).map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={t('fields.unit')}
              value={form.unit}
              onChange={(event) =>
                setForm({ ...form, unit: event.target.value as FormState['unit'] })
              }
              fullWidth
            >
              {PRODUCT_UNITS.map((unit) => (
                <MenuItem key={unit} value={unit}>
                  {t(`units.${unit}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('fields.unitsPerCase')}
              value={form.unitsPerCase}
              onChange={(event) => setForm({ ...form, unitsPerCase: event.target.value })}
              error={Boolean(fieldErrors.unitsPerCase)}
              helperText={fieldErrors.unitsPerCase ? t(fieldErrors.unitsPerCase) : undefined}
              inputMode="numeric"
              fullWidth
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label={t('fields.priceNet')}
              value={form.price}
              onChange={(event) => setForm({ ...form, price: event.target.value })}
              error={Boolean(fieldErrors.price)}
              helperText={
                fieldErrors.price ? t(fieldErrors.price) : t('products.priceNetHelp')
              }
              inputMode="decimal"
              required
              fullWidth
            />
            <TextField
              label={t('fields.vatRate')}
              value={form.vatRate}
              onChange={(event) => setForm({ ...form, vatRate: event.target.value })}
              error={Boolean(fieldErrors.vatRate)}
              helperText={fieldErrors.vatRate ? t(fieldErrors.vatRate) : undefined}
              inputMode="numeric"
              required
              fullWidth
            />
            <TextField
              select
              label={t('fields.status')}
              value={form.isActive ? 'true' : 'false'}
              onChange={(event) => setForm({ ...form, isActive: event.target.value === 'true' })}
              fullWidth
            >
              <MenuItem value="true">{t('common.active')}</MenuItem>
              <MenuItem value="false">{t('common.inactive')}</MenuItem>
            </TextField>
          </Stack>

          <TextField
            label={t('fields.notes')}
            value={form.notes}
            onChange={(event) => setForm({ ...form, notes: event.target.value })}
            multiline
            minRows={2}
            fullWidth
          />
        </Stack>
      </FormDialog>

      <ConfirmDialog
        open={Boolean(deleting)}
        title={t('products.deleteTitle')}
        message={t('products.deleteMessage', { name: deleting?.name ?? '' })}
        onCancel={() => setDeleting(null)}
        onConfirm={() => void confirmDelete()}
        submitting={crud.remove.isPending}
        errorKey={deleteError.errorKey}
      />
    </>
  );
}
